// ---------------------------------------------------------------------------
// Compact relative time for the sidebar's RECENT SEGMENTS (AL-1850)
// ---------------------------------------------------------------------------
// CT-0 writes these as "2m", "1h", "Yest". Rather than invent an abbreviation
// table per language, this leans on Intl.RelativeTimeFormat in its narrow
// style, which is what each locale actually abbreviates to ("2m ago",
// "há 2 min.") — and falls back to a short date once the unit stops being
// useful.
//
// The repo already has `formatRelativeTimeInUserTimezone`, but it writes
// Portuguese into the string ("2h atrás") and cannot be translated.
// ---------------------------------------------------------------------------

import { toIntlLocale, formatLocalDay } from '@/lib/format';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

export function relativeShort(iso: string, language: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const locale = toIntlLocale(language);
  const elapsed = now.getTime() - date.getTime();

  if (elapsed >= WEEK_MS) {
    return formatLocalDay(date, locale);
  }

  if (elapsed < MINUTE_MS) return formatAgo(locale, 0, 'minute');
  if (elapsed < HOUR_MS) return formatAgo(locale, -Math.floor(elapsed / MINUTE_MS), 'minute');
  if (elapsed < DAY_MS) return formatAgo(locale, -Math.floor(elapsed / HOUR_MS), 'hour');
  return formatAgo(locale, -Math.floor(elapsed / DAY_MS), 'day');
}

/**
 * The narrow style, unless the locale's narrow form is a bare minus sign.
 *
 * CLDR writes pt-PT's narrow past as "-1 h" rather than "há 1 h", which in the
 * sidebar read as a negative time. The short style says "há 1 h" there and is
 * barely longer, so it stands in whenever narrow comes out signed.
 */
function formatAgo(locale: string, value: number, unit: Intl.RelativeTimeFormatUnit): string {
  const narrow = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' }).format(
    value,
    unit
  );
  if (!/^[-\u2212]/.test(narrow)) return narrow;
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' }).format(
    value,
    unit
  );
}
